/**
 * useItemEdit Hook
 * Manages item edit modal state and operations
 * Extracted from InventoryView to reduce file size and improve testability
 */

import { useState, useRef, useCallback } from 'react';
import { EditableItem } from '../components/itemEdit';
import { saveItem, DuplicateInfo } from '../services/inventoryService';

export interface UseItemEditOptions {
  token: string | null;
  onSuccess?: () => Promise<void> | void;
  checkResponseForLimit?: (response: Response) => Promise<boolean>;
}

export interface UseItemEditReturn {
  // State
  editItem: EditableItem | null;
  editOpen: boolean;
  saving: boolean;
  editItemRef: React.MutableRefObject<EditableItem | null>;
  
  // Actions
  setEditItem: (item: EditableItem | null) => void;
  setEditOpen: (open: boolean) => void;
  setSaving: (saving: boolean) => void;
  openEdit: (item: EditableItem) => void;
  closeEdit: () => void;
  updateEditItem: (patch: Partial<EditableItem>) => void;
  saveEditItem: (options?: {
    pendingImageId?: number;
    duplicateCollectionId?: number;
  }) => Promise<{ success: boolean; error?: string; itemId?: number; duplicateInfo?: DuplicateInfo }>;
}

/**
 * Custom hook for managing item edit state
 * 
 * @param options - Configuration options
 * @returns Edit state and actions
 */
export function useItemEdit(options: UseItemEditOptions): UseItemEditReturn {
  const { token, onSuccess, checkResponseForLimit } = options;
  
  const [editItem, setEditItem] = useState<EditableItem | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const editItemRef = useRef<EditableItem | null>(null);

  /**
   * Open edit modal with an item
   */
  const openEdit = useCallback((item: EditableItem) => {
    setEditItem(item);
    editItemRef.current = item;
    setEditOpen(true);
  }, []);

  /**
   * Close edit modal
   */
  const closeEdit = useCallback(() => {
    setEditOpen(false);
    setEditItem(null);
    editItemRef.current = null;
  }, []);

  /**
   * Update edit item with partial changes
   * Updates both state and ref for consistency
   */
  const updateEditItem = useCallback((patch: Partial<EditableItem>) => {
    setEditItem((prev) => {
      const updated = prev ? { ...prev, ...patch } : prev;
      editItemRef.current = updated;
      return updated;
    });
  }, []);

  /**
   * Save the currently edited item
   * 
   * @param options - Additional save options (image ID, collection ID)
   * @returns Result with success status and optional error message
   */
  const saveEditItem = useCallback(async (options?: {
    pendingImageId?: number;
    duplicateCollectionId?: number;
  }): Promise<{ success: boolean; error?: string; itemId?: number; duplicateInfo?: DuplicateInfo }> => {
    const currentItem = editItemRef.current;
    
    if (!token || !currentItem) {
      return { success: false, error: 'Missing token or item' };
    }

    try {
      setSaving(true);
      
      const isNewItem = currentItem.id === -1;
      const pendingImageId = options?.pendingImageId ?? (currentItem as any).pendingImageId;
      const duplicateCollectionId = options?.duplicateCollectionId ?? (currentItem as any).__duplicateCollectionId;

      // Use unified save service
      const result = await saveItem({
        token,
        item: currentItem,
        isNewItem,
        pendingImageId,
        duplicateCollectionId,
        checkResponseForLimit
      });

      if (!result.success) {
        if (result.error === 'Item limit reached') {
          // Limit modal already shown by checkResponseForLimit
          return { success: false, error: 'Item limit reached' };
        }
        if (result.duplicateInfo) {
          return { success: false, error: result.error, duplicateInfo: result.duplicateInfo };
        }
        return { success: false, error: result.error || 'Failed to save item' };
      }

      // Success - call onSuccess callback if provided
      if (onSuccess) {
        await onSuccess();
      }

      // Return the item ID so caller can scroll to it
      const itemId = isNewItem ? result.newItemId : currentItem.id;
      return { success: true, itemId };
    } catch (e: any) {
      console.error('[useItemEdit] Save error:', e);
      return {
        success: false,
        error: e.message || 'Failed to save item. Please try again.'
      };
    } finally {
      setSaving(false);
    }
  }, [token, onSuccess, checkResponseForLimit]);

  return {
    // State
    editItem,
    editOpen,
    saving,
    editItemRef,
    
    // Actions
    setEditItem,
    setEditOpen,
    setSaving,
    openEdit,
    closeEdit,
    updateEditItem,
    saveEditItem,
  };
}
